import { MenuIcon } from '@/components/MenuIcon';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { fileSize, shortDate } from '@/lib/format';
import { useDocuments, useDownloadDocument } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 받은 문서.
 *
 * <p>누르면 <b>열람 증적</b>이 먼저 남고(DOC-82 §7 — `external_audit_events` 의 `DOCUMENT_VIEW`)
 * 파일이 내려온다. 「보냈다」 가 아니라 「열었다」 가 증적이라, 공지가 실제로 닿았는지를 이것으로
 * 확인한다. 인쇄가 아니라 <b>다운로드</b>다(DOC-84 §3-D-2).
 *
 * <p>보이는 문서는 서버가 잘라 준 것뿐이다 — 전사 문서(`site_id IS NULL`)는 외부에 열리지 않고,
 * 분류 코드가 외부 공개 목록에 있는 것만 온다(§3-6).
 */
export function DocumentsPage() {
  const { siteId, siteName } = useSite();
  const documents = useDocuments(siteId);
  const download = useDownloadDocument(siteId);
  const rows = documents.data ?? [];

  return (
    <>
      <PageHeader title="문서" subtitle={siteName} />
      <QueryState
        pending={documents.isPending}
        error={documents.isError}
        empty={rows.length === 0}
        emptyText="받은 문서가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => {
            const busy = download.isPending && download.variables?.id === row.id;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => download.mutate({ id: row.id, fileName: row.fileName })}
                  disabled={download.isPending}
                  className="card flex w-full items-center justify-between gap-3 text-left disabled:opacity-60"
                >
                  <div className="min-w-0">
                    <div className="truncate text-ui-md">{row.fileName}</div>
                    <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {row.categoryName} · {shortDate(row.createdAt)} · {fileSize(row.size)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {row.viewedAt === null && <span className="badge badge-blue">새 문서</span>}
                    <span style={{ color: 'var(--text-muted)' }}>
                      <MenuIcon name={busy ? 'clipboard' : 'download'} size={16} />
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </QueryState>
    </>
  );
}
